import Decimal from 'decimal.js'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  GetDistanceCalculationQuery,
  GoogleDistanceStatus,
  useGetDistanceCalculationLazyQuery,
} from 'generated/graphql'
import { getGoogleAddressInput, getReturnLegAddressInput } from 'helpers'
import { TBookingForm } from 'types/form-types'

export const useRecalculateDistance = () => {
  const { getValues, setValue } = useFormContext<TBookingForm>()

  const handleDistanceCalculation = (
    data: GetDistanceCalculationQuery,
    distanceType: 'distance' | 'returnDistance',
  ) => {
    const { status, distance: rawDistance, errorMessage } = data.distanceCalculation

    if (status === GoogleDistanceStatus.Success) {
      const distance = new Decimal(rawDistance).toDP(2, Decimal.ROUND_FLOOR).toNumber()

      setValue(distanceType, distance, { shouldDirty: true, shouldValidate: true })
    } else if (status === GoogleDistanceStatus.Error) {
      toast.warning(errorMessage)
    }
  }

  const [getDistanceCalculation, { loading: distanceCalculationLoading }] =
    useGetDistanceCalculationLazyQuery({
      onError: (error) => {
        toast.error(error.message)
      },
    })

  const recalculateDistance = () => {
    const { collectionAddresses, deliveryAddresses, isWaitAndReturn } = getValues()

    if (!collectionAddresses?.length || !deliveryAddresses?.length) {
      return
    }

    const googleAddressInput = getGoogleAddressInput(collectionAddresses, deliveryAddresses)

    if (googleAddressInput.length < 2) {
      return
    }

    getDistanceCalculation({ variables: { input: googleAddressInput } }).then(
      ({ data }) => data && handleDistanceCalculation(data, 'distance'),
    )

    const returnLeg = isWaitAndReturn
      ? getReturnLegAddressInput(collectionAddresses, deliveryAddresses)
      : null

    if (returnLeg) {
      getDistanceCalculation({ variables: { input: returnLeg } }).then(
        ({ data }) => data && handleDistanceCalculation(data, 'returnDistance'),
      )
    } else {
      setValue('returnDistance', 0, { shouldDirty: true })
    }
  }

  return { recalculateDistance, distanceCalculationLoading }
}
